import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
} from "react";
import { View, Text, TouchableOpacity, StyleSheet } from "react-native";
import { router } from "expo-router";
import { getStreamClient } from "@/services/stream/client";
import { parseStartCallEvent, subscribeStartCall, type StartCallParams } from "@/services/calls/startCallEvent";
import { StartCallPrompt } from "@/components/calls/StartCallPrompt";

const isDesktop = typeof window !== "undefined" && !!window.desktop;

/**
 * A `start_call` request parked across a page reload.
 *
 * Right after the CRM pushes the event it opens the launcher page, and in the
 * desktop shell that deep link is handled with `win.loadURL(APP_URL?channelId=…)`
 * — a full reload that would wipe the prompt the event just opened. So the
 * request is stored on arrival and shown again once the app has booted, as
 * long as it is fresh. Browser storage can be missing or throw (private mode,
 * previews), hence the guards.
 */
const PENDING_START_CALL_KEY = "nomadas.pendingStartCall";
const PENDING_START_CALL_TTL_MS = 60_000;

function savePendingStartCall(params: StartCallParams) {
    try {
        localStorage.setItem(PENDING_START_CALL_KEY, JSON.stringify({ params, at: Date.now() }));
    } catch {
        // No storage — the prompt still shows in this page; it just won't survive a reload.
    }
}

function clearPendingStartCall() {
    try {
        localStorage.removeItem(PENDING_START_CALL_KEY);
    } catch {
        // Nothing to clear.
    }
}

/** Returns a still-fresh parked request (and clears it), or `null`. */
function takePendingStartCall(): StartCallParams | null {
    try {
        const raw = localStorage.getItem(PENDING_START_CALL_KEY);
        if (!raw) return null;
        localStorage.removeItem(PENDING_START_CALL_KEY);
        const { params, at } = JSON.parse(raw) as { params?: unknown; at?: number };
        if (typeof at !== "number" || Date.now() - at > PENDING_START_CALL_TTL_MS) return null;
        // Re-validate through the same parser the live event goes through.
        return parseStartCallEvent({ call: params });
    } catch {
        return null;
    }
}

interface NotificationContextType {
    expoPushToken: string | null;
    notification: null;
    error: Error | null;
    unreadCount: number;
    setUnreadCount: (count: number) => void;
    chatUnreadCount: number;
    setChatUnreadCount: (count: number) => void;
    notificationsBlocked: boolean;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (context === undefined) {
        return {
            expoPushToken: null,
            notification: null,
            error: null,
            unreadCount: 0,
            setUnreadCount: () => {},
            chatUnreadCount: 0,
            setChatUnreadCount: () => {},
            notificationsBlocked: false,
        };
    }
    return context;
};

function fireNotification(title: string, body: string) {
    console.log("[notifications] fireNotification called", { title, body, isDesktop });
    if (!isDesktop) return;
    window.desktop!.notify({ title, body });
}

interface NotificationProviderProps {
    children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
    const [unreadCount, setUnreadCount] = useState(0);
    const [chatUnreadCount, setChatUnreadCount] = useState(0);
    const [notificationsBlocked, setNotificationsBlocked] = useState(false);
    const [bannerDismissed, setBannerDismissed] = useState(false);
    // Pending CRM "call this client" request, shown by StartCallPrompt.
    const [startCallRequest, setStartCallRequest] = useState<StartCallParams | null>(null);

    // Determine the OS notification state on mount: subscribe to push updates AND pull
    // the current status once (race-free). The push can fire before this listener is
    // registered on an already-denied relaunch, so the pull is what reliably shows the
    // banner at launch.
    useEffect(() => {
        if (!isDesktop) return;

        window.desktop!.onNotificationsBlocked(() => {
            console.log("[notifications] notifications are blocked by the OS");
            setNotificationsBlocked(true);
        });

        let cancelled = false;
        (async () => {
            try {
                const status = await window.desktop!.getNotificationStatus?.();
                if (!cancelled && status?.blocked) {
                    console.log("[notifications] initial status: blocked");
                    setNotificationsBlocked(true);
                }
            } catch {
                // Older shell without getNotificationStatus — the push path still covers it.
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    // Sync badge count with the desktop shell whenever it changes
    useEffect(() => {
        if (isDesktop) {
            window.desktop!.setBadge(chatUnreadCount);
        }
    }, [chatUnreadCount]);

    useEffect(() => {
        const client = getStreamClient();

        // notification.message_new → user is NOT watching this channel, always notify
        const handleNotificationNew = (event: any) => {
            console.log("[notifications] notification.message_new", event);
            if (event.total_unread_count !== undefined) {
                setChatUnreadCount(event.total_unread_count);
            }

            const message = event.message;
            if (!message) return;

            const senderName: string = message.user?.name || "New message";
            const text: string = message.text || "You have a new message";

            fireNotification(senderName, text);
        };

        // message.new → fires for every channel the client is watching (the inbox watches
        // them all), so this is the main path. Suppress a notification ONLY when the user
        // is focused AND already looking at this exact channel.
        // NOTE: document.hidden is unreliable in Electron — switching to another app does
        // not set it while the window stays on-screen — so we use document.hasFocus()
        // plus the currently-open channel id (set by the inbox screen).
        const handleMessageNew = (event: any) => {
            if (event.total_unread_count !== undefined) {
                setChatUnreadCount(event.total_unread_count);
            }

            const message = event.message;
            if (!message) return;

            // Never notify for our own messages.
            if (message.user?.id === client.userID) return;

            const activeChannelId = (global as any).__CURRENT_CHANNEL_ID__ ?? null;
            const eventChannelId =
                event.channel_id ?? (event.cid ? String(event.cid).split(":").pop() : null);
            const viewingThisChannel =
                document.hasFocus() && !!activeChannelId && eventChannelId === activeChannelId;

            console.log("[notifications] message.new", {
                focused: document.hasFocus(),
                activeChannelId,
                eventChannelId,
                viewingThisChannel,
            });

            if (viewingThisChannel) return;

            const senderName: string = message.user?.name || "New message";
            const text: string = message.text || "You have a new message";

            fireNotification(senderName, text);
        };

        // new_lead → Stream "user custom event" sent by the Universe backend
        // (StreamChannel notification channel). Payload: { type, title, body,
        // data: { inscription_id, is_guide_finder, type }, unread_count }.
        const handleNewLead = (event: any) => {
            console.log("[notifications] new_lead", event);

            if (typeof event.unread_count === "number") {
                setUnreadCount(event.unread_count);
            }

            fireNotification(event.title || "New lead", event.body || "");
        };

        const handleMarkRead = (event: any) => {
            if (event.total_unread_count !== undefined) {
                setChatUnreadCount(event.total_unread_count);
            }
        };

        const handleConnectionChange = (event: any) => {
            const eventUser = event.me || event.user || client.user;
            if (event.online && eventUser?.total_unread_count !== undefined) {
                setChatUnreadCount(eventUser.total_unread_count);
            }
        };

        client.on("notification.message_new", handleNotificationNew);
        client.on("message.new", handleMessageNew);
        client.on("new_lead" as any, handleNewLead);
        client.on("notification.mark_read", handleMarkRead);
        client.on("message.read", handleMarkRead);
        client.on("connection.changed", handleConnectionChange);

        // start_call → sent by the Universe CRM when the guide clicks a client's
        // number. Opens the "Do you want to start to call?" prompt; the call
        // itself starts from there (see startCall below). Parked in storage too,
        // because the launcher the CRM opens next reloads the desktop window.
        const unsubscribeStartCall = subscribeStartCall(client, (params) => {
            savePendingStartCall(params);
            setStartCallRequest(params);
        });

        // …and this is the other side of that reload: show the request that was
        // parked just before it.
        const pending = takePendingStartCall();
        if (pending) {
            console.log("[calls] restoring start_call after reload", { channel_id: pending.channel_id });
            setStartCallRequest(pending);
        }

        // Seed the count if the user is already connected
        const user = client.user as any;
        if (user?.total_unread_count !== undefined) {
            setChatUnreadCount(user.total_unread_count);
        }

        return () => {
            client.off("notification.message_new", handleNotificationNew);
            client.off("message.new", handleMessageNew);
            client.off("new_lead" as any, handleNewLead);
            client.off("notification.mark_read", handleMarkRead);
            client.off("message.read", handleMarkRead);
            client.off("connection.changed", handleConnectionChange);
            unsubscribeStartCall();
        };
    }, []);

    const showBanner = notificationsBlocked && !bannerDismissed;

    // Open the call exactly as the channel header does: the dedicated window in
    // the desktop shell, in-app navigation in a plain browser (or an older shell
    // without openCall). Params are all strings — the openCall contract.
    const startCall = (params: StartCallParams) => {
        if (window.desktop?.openCall) {
            window.desktop.openCall(params);
        } else {
            router.push({ pathname: '/call', params });
        }
    };

    return (
        <NotificationContext.Provider
            value={{
                expoPushToken: null,
                notification: null,
                error: null,
                unreadCount,
                setUnreadCount,
                chatUnreadCount,
                setChatUnreadCount,
                notificationsBlocked,
            }}
        >
            {children}
            <StartCallPrompt
                request={startCallRequest}
                onClose={() => {
                    clearPendingStartCall();
                    setStartCallRequest(null);
                }}
                onStartCall={startCall}
            />
            {showBanner && (
                <View style={styles.banner}>
                    <Text style={styles.bannerText}>
                        🔔 Notifications are blocked.{" "}
                        <Text
                            style={styles.bannerLink}
                            onPress={() => window.desktop?.openSettings()}
                        >
                            Open Settings
                        </Text>
                        {" "}to enable them.
                    </Text>
                    <TouchableOpacity onPress={() => setBannerDismissed(true)}>
                        <Text style={styles.bannerDismiss}>✕</Text>
                    </TouchableOpacity>
                </View>
            )}
        </NotificationContext.Provider>
    );
};

const styles = StyleSheet.create({
    banner: {
        position: "absolute",
        bottom: 24,
        left: 16,
        right: 16,
        backgroundColor: "#1C1C1E",
        borderRadius: 12,
        paddingVertical: 12,
        paddingHorizontal: 16,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        // web shadow
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
    },
    bannerText: {
        flex: 1,
        color: "#FFFFFF",
        fontSize: 13,
        lineHeight: 18,
    },
    bannerLink: {
        fontWeight: "700",
        color: "#14a5c6",
        textDecorationLine: "underline",
    },
    bannerDismiss: {
        color: "#8E8E93",
        fontSize: 16,
        paddingLeft: 8,
    },
});
